import { getKpisForProvidersWithCategoryChart } from "../opendata";
import { CategoryChartQueryParam } from "../categoryChart";
import { TableBase, TableKpiProvider } from "./categoryChartData.types";
import { Category, getBaseValue, KpiReport, LongText, roundValue, Unit, ValueStatus } from "../processors";
import {
    EMPTY_ARRAY,
    VALUE_STATUS_NAMING,
    WaterProvider,
    WATER_SECURITY_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY_VALUE_TO_LABEL
} from "../misc";
import _ from "lodash";
import { getLostSuppliedVolumnPercent } from "./fetchFigureLostSuppliedVolumePercentChartData";
import { getProportionOfAssetValue } from "./fetchFigureSolidDashProportionOfAssetValueChartData";
import { getOperatingSurplus } from "./fetchFigureSolidDashOperatingSurplusRatioChartData";
import { getRealERateReturn } from "./fetchFigureSolidDashRealERateReturnKpiProviderChartData";
import { Column } from "react-table";
import { formatTableColumnNumber, uppercaseFirst } from "../../utils";
import { getWaterSuppliedPerResidentialConnection } from "./fetchFigureWaterSuppliedPerResidentialConnectionChartData";

export type TableMethod =
    | "count"
    | "sum"
    | "avg"
    | "perConnection"
    | "getAnnualBill"
    | "getAvailableMonthLabelWSSupplyDemand"
    | "getLostSuppliedVolumePercent"
    | "getAssetValueProportion"
    | "getOperatingSurplusRatio"
    | "getEconRealRateReturn"
    | "getWaterSuppliedPerResidentialConnection"
    | "first"
    | "getOtherFromSum"
    | "displayWaterImportedIfEnabled"
    | "sumWithWaterImportedIfEnabled"
    | "getActualMainBreaks";

const calculateLogic = ({
    method,
    category,
    baseValues,
    provider,
    kpis
}: {
    category: Category;
    method: TableMethod;
    baseValues: KpiReport[];
    provider: WaterProvider;
    kpis: string[][];
}): {
    value: number | ValueStatus;
    unit: Unit;
    isValid: boolean;
    unitText: Unit;
    typeClassName: string;

    // this field to inform the roundValue function to convert
    // units consistently for all cell values
    // if you're not happy with base value units, make this to true, otherwise, keep it false
    unitTransformForTableColumnDisplay: boolean;
} => {
    // when method is custom,
    // it returns value, unit, valid, unit text, type class name

    let _value: number | ValueStatus = null;
    let _unit: Unit = null;
    let _isValid = true;
    let _unitText: Unit = null;
    let _typeClassName = "string-mid";
    const _nonNullBaseValues = baseValues.filter(d => d.value != null);
    const _nonNullBaseValueStatus = baseValues.filter(d => d.valueStatus != null);

    // by default, all column cell value units will be consitently converted to a certain scale
    let unitTransformForTableColumnDisplay = true;
    const includeImportedWater = provider?.includeImportedWater ?? false;

    if (method === "displayWaterImportedIfEnabled") {
        if (!includeImportedWater) {
            _value = null;
            _unit = null;
            _unitText = null;
        } else {
            method = "sum";
        }
    }

    if (method === "sumWithWaterImportedIfEnabled") {
        const totalKpis = kpis?.filter((__, index) => index === 0);
        const waterImportedKpis = kpis?.filter((__, index) => index > 0);
        const totalKpisMap = totalKpis?.reduce((prev, cur) => {
            prev[cur?.[0]] = 1;
            return prev;
        }, {});
        const waterImportedKpisMap = waterImportedKpis?.reduce((prev, cur) => {
            prev[cur?.[0]] = 1;
            return prev;
        }, {});

        const totalValues = baseValues.filter(d => totalKpisMap[d.kpiCode] === 1);
        const waterImportedValues = baseValues.filter(d => waterImportedKpisMap[d.kpiCode] === 1);

        const totalRes = calculateLogic({
            method: "sum",
            category,
            baseValues: totalValues,
            provider,
            kpis: totalKpis
        });

        const waterImportedRes = calculateLogic({
            method: "sum",
            category,
            baseValues: waterImportedValues,
            provider,
            kpis: waterImportedKpis
        });

        if (!includeImportedWater) {
            // calculateLogic({
            //     method: "sum",
            //     category,
            //     baseValues: totalValues,
            //     provider,
            //     kpis: totalKpis
            // }

            _value =
                typeof totalRes?.value === "number" && typeof waterImportedRes?.value === "number"
                    ? totalRes?.value - waterImportedRes?.value
                    : totalRes?.value;
            _unit = totalRes?.unit;
            _unitText = totalRes?.unitText;
        } else {
            _value = totalRes?.value;
            _unit = totalRes?.unit;
            _unitText = totalRes?.unitText;
        }
    }

    if (method === "count") {
        _value = baseValues?.length;

        _unit = "Count";
        _unitText = null;
        // _isValid = true;
    }

    if (method === "getOtherFromSum") {
        // total kpi
        const summaryKpi = kpis?.[0]?.[0];

        // contrib kpis
        const elementKpis = kpis?.filter((___, index) => index > 0)?.map(d => d?.[0]);
        const elementKpiMap = elementKpis.reduce((prev, cur) => {
            prev[cur] = 1;
            return prev;
        }, {});

        const summaryValueStatus = _nonNullBaseValueStatus.find(d => d.kpiCode === summaryKpi)?.valueStatus;

        if (summaryValueStatus != null) {
            // total kpi has value status, return value status for other kpi
            _value = summaryValueStatus;
            _unit = _.uniq(baseValues?.map(d => d.unit))?.[0] as Unit;
            _unitText = _unit;
        } else {
            // other exlude contrib kpi values from total to get the other kpi value
            const summaryValue = _.sum(_nonNullBaseValues.filter(d => d.kpiCode === summaryKpi).map(d => d.value ?? 0));
            const totalElementsValue = _.sum(
                _nonNullBaseValues.filter(d => elementKpiMap[d.kpiCode] === 1).map(d => d.value ?? 0)
            );

            _value = Math.max(summaryValue - totalElementsValue, 0);
            _unit = _.uniq(baseValues?.map(d => d.unit))?.[0] as Unit;
            _unitText = _unit;
        }
    }

    if (method === "sum" || method === "avg") {
        _value =
            !_nonNullBaseValues?.length && !_nonNullBaseValueStatus.length
                ? null
                : !_nonNullBaseValues?.length
                ? _.uniq(_nonNullBaseValueStatus?.map(d => d.valueStatus))?.[0] ??
                  _nonNullBaseValueStatus?.[0]?.valueStatus
                : _?.[method](_nonNullBaseValues.map(d => d?.value ?? 0));
        _unit = _.uniq(baseValues?.map(d => d.unit))?.[0] as Unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getAvailableMonthLabelWSSupplyDemand") {
        const { value: monthValue } = calculateLogic({
            method: "sum",
            category,
            baseValues,
            provider,
            kpis
        });

        _value =
            typeof monthValue === "number"
                ? WATER_SECURITY_SUPPLY_DEMAND_MONTHS_OF_AVAILABLE_SUPPLY_VALUE_TO_LABEL?.[monthValue]
                : monthValue;

        _unit = "" as Unit;
        _unitText = _unit;
    }

    if (method === "first") {
        _value =
            !_nonNullBaseValueStatus?.length && !_nonNullBaseValues?.length
                ? null
                : _nonNullBaseValues?.[0]?.value ?? _nonNullBaseValueStatus?.[0]?.valueStatus;

        _unit = _.uniq(baseValues?.map(d => d.unit))?.[0] as Unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getActualMainBreaks") {
        const mainsBreak = kpis?.[0]?.[0];
        const waterMainsBreakLength = kpis?.[1]?.[0];

        _value =
            !_nonNullBaseValueStatus?.length && !_nonNullBaseValues?.length
                ? null
                : _nonNullBaseValueStatus?.length
                ? _nonNullBaseValueStatus?.[0]?.valueStatus
                : ((_nonNullBaseValues?.find(d => d.kpiCode === mainsBreak)?.value ?? 0) *
                      (_nonNullBaseValues?.find(d => d.kpiCode === waterMainsBreakLength)?.value ?? 0)) /
                  100;

        _unit = "Count";
        // _nonNullBaseValues?.find(d => d.kpiCode === mainsBreak)?.unit as Unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getAnnualBill") {
        if (
            provider?.residentialTariffStruct === "fixed" ||
            provider?.residentialTariffStruct === "noChargeToResidents" ||
            provider?.residentialTariffStruct === "consumptionCharge" ||
            provider?.residentialTariffStruct === "fixedEntitlementAndExcessConsumptionCharge"
        ) {
            _value = null;
            _unit = null;
            _unitText = null;
        } else {
            _value = !_nonNullBaseValues?.length ? null : _?.sum(_nonNullBaseValues.map(d => d?.value ?? 0));
            _unit = _.uniq(baseValues?.map(d => d.unit))?.[0] as Unit;
            _unitText = _unit;
        }
    }

    if (method === "perConnection") {
        const connectionSizeKPIs = kpis?.filter((__, index) => index > 0);
        const reportKPIs = kpis?.filter((__, index) => index <= 0);
        const connectionSizeKPIMap = connectionSizeKPIs?.reduce((prev, cur) => {
            prev[cur?.[0]] = 1;
            return prev;
        }, {});
        const reportKPIMap = reportKPIs?.reduce((prev, cur) => {
            prev[cur?.[0]] = 1;
            return prev;
        }, {});

        const reportValues = _nonNullBaseValues.filter(d => reportKPIMap[d.kpiCode] === 1);
        const connectionSizeValues = _nonNullBaseValues.filter(d => connectionSizeKPIMap[d.kpiCode] === 1);

        _value = !reportValues?.length ? null : _.sum(reportValues.map(d => d?.value ?? 0));
        _unit = _.uniq(reportValues?.map(d => d.unit))?.[0] as Unit;
        _unitText = _unit;

        const conSize = !connectionSizeValues?.length ? null : _.sum(connectionSizeValues.map(d => d?.value ?? 0));

        // const conSize =
        //     category == null
        //         ? null
        //         : category?.isSewerageServices
        //         ? provider?.sewerageConnectionSize
        //         : provider?.connectionSize;

        _value = _value != null && conSize != null ? _value / conSize : null;
        // _isValid = _value != null;
    }

    if (method === "getLostSuppliedVolumePercent") {
        const result = !_nonNullBaseValues?.length ? null : getLostSuppliedVolumnPercent(kpis, _nonNullBaseValues);
        _value = result?.value;
        _unit = result?.unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getAssetValueProportion") {
        const result = !_nonNullBaseValues?.length ? null : getProportionOfAssetValue(kpis, _nonNullBaseValues);
        _value = result?.value;
        _unit = result?.unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getOperatingSurplusRatio") {
        const result = !_nonNullBaseValues?.length ? null : getOperatingSurplus(kpis, _nonNullBaseValues);
        _value = result?.value;
        _unit = result?.unit;
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getEconRealRateReturn") {
        _value = !_nonNullBaseValues?.length ? null : getRealERateReturn(provider, kpis, _nonNullBaseValues, true);
        _unit = "%";
        _unitText = _unit;
        // _isValid = true;
    }

    if (method === "getWaterSuppliedPerResidentialConnection") {
        const result = !_nonNullBaseValues?.length
            ? null
            : getWaterSuppliedPerResidentialConnection(kpis, _nonNullBaseValues);

        _value = result?.value;
        _unit = result?.unit;
        _unitText = _unit;

        // we want to keep base unit (kL), so don't want unit transform later
        unitTransformForTableColumnDisplay = false;
    }

    _unitText = _unit === "Count" ? null : _unit;
    _isValid = _value != null;

    // console.log(_isValid, _value, method);
    return {
        value: _value,
        unit: _unit,
        isValid: _isValid,
        unitText: _unitText,
        typeClassName: _typeClassName,
        unitTransformForTableColumnDisplay
    };
};

export const kpiCodeSplitter = "-------";

export const getTable = async ({
    fetchParams,
    categoryChart,
    providers,
    kpis
}: CategoryChartQueryParam): Promise<
    (TableBase & {
        header?: LongText;
        footer?: LongText;
    })[][]
> => {
    if (!providers || providers?.length === 0 || !categoryChart || !kpis || kpis?.length === 0) return null;

    // one kpi for WSP, one for Potable water scheme
    // if (kpis?.length !== 2) return null;

    // method supports per connection, sum, avg, count, kpiCode supports fixed code that requires a sequence of codes
    // kpiCode: QG 1.23-------QG 2.33;

    const yearStart = fetchParams?.[0] == null ? null : parseInt(fetchParams[0]);
    const yearEnd = fetchParams?.[1] == null ? null : parseInt(fetchParams[1]);

    const providerKpis = kpis?.filter(
        d => d?.[4] == null || d?.[4] === "both" || d?.[4] === "provider" || d?.[4] === "both-scheme-provider"
    );
    const providerKpiParams = _.uniqBy(
        providerKpis?.map(d => {
            return {
                kpiCode: d[0],
                subCodes: d[0]
                    .split(kpiCodeSplitter)
                    .filter(d => d != null && d?.trim() !== "")
                    .map(d => d?.trim()),
                schemeType: d[1],
                name: d[2],
                method: d[3] ?? "sum"
            };
        }),
        d => _.join([d.kpiCode, d.schemeType, d.method], "###")
    );
    const providerKpisForOpendataRequest = _.uniqBy(
        _.flattenDeep(
            providerKpiParams?.map(d => {
                return [
                    ...d.subCodes.map(subCode => {
                        return { kpiCode: subCode, schemeType: d.schemeType };
                    })
                ];
            })
        ),
        d => _.join([d.kpiCode, d.schemeType], "###")
    ).map(d => {
        return [d.kpiCode, d.schemeType];
    });
    // .reduce((prev, curr) => {
    //     prev = [...prev, ...curr];
    //     return prev;
    // }, []);

    const schemeKpis = _.flatten(
        kpis
            ?.filter(
                d => d?.[4] == null || d?.[4] === "both" || d?.[4] === "scheme" || d?.[4] === "both-scheme-provider"
            )
            ?.map(d => {
                const kpi = [...d];

                const potableSchemeKpis = [...kpi];
                potableSchemeKpis[1] = "Potable water scheme";

                if (d[4] === "both-scheme-provider") {
                    const potableSchemeKpis = [...kpi];
                    potableSchemeKpis[1] = "Potable water scheme";

                    const partiallyTreatedWaterSchemeKpis = [...kpi];
                    partiallyTreatedWaterSchemeKpis[1] = "Raw-Partially treated water scheme";

                    return [potableSchemeKpis, partiallyTreatedWaterSchemeKpis];
                }

                return [potableSchemeKpis];
                // kpi[1] =
                //     d?.[4] === "partially-treated-scheme" ? "Raw-Partially treated water scheme" : "Potable water scheme";
                // return kpi;
            })
    );

    const schemeKpiParams = _.uniqBy(
        schemeKpis?.map(d => {
            return {
                kpiCode: d[0],
                subCodes: d[0].split(kpiCodeSplitter).filter(d => d != null && d?.trim() !== ""),
                schemeType: d[1],
                name: d[2],
                method: d[3] ?? "sum"
            };
        }),
        d => _.join([d.kpiCode, d.schemeType, d.method], "###")
    );

    const schemeKpisForOpendataRequest = _.uniqBy(
        _.flattenDeep(
            schemeKpiParams?.map(d => {
                return [
                    ...d.subCodes.map(subCode => {
                        return { kpiCode: subCode, schemeType: d.schemeType };
                    })
                ];
            })
        ),
        d => _.join([d.kpiCode, d.schemeType], "###")
    ).map(d => {
        return [d.kpiCode, d.schemeType];
    });

    // _.uniqBy(schemeKpiParams, d => _.join([d.kpiCode, d.schemeType], "###"))
    //     ?.map(d => {
    //         return [
    //             ...d.subCodes.map(subCode => {
    //                 return [subCode, d.schemeType];
    //             })
    //         ];
    //     })
    //     .reduce((prev, curr) => {
    //         prev = [...prev, ...curr];
    //         return prev;
    //     }, []);

    const results = await getKpisForProvidersWithCategoryChart(
        providerKpisForOpendataRequest,
        providers,
        false,
        categoryChart
    );
    const pwsResults = await getKpisForProvidersWithCategoryChart(
        schemeKpisForOpendataRequest,
        providers,
        false,
        categoryChart
    );

    const schemeTypes = [
        {
            // Potable schemes
            extractScheme: false,
            providerTidToResults: _.groupBy(results, d => d?.provider?.tid)
        },
        {
            // Potable schemes
            extractScheme: true,
            providerTidToResults: _.groupBy(pwsResults, d => d?.provider?.tid)
        }
    ];

    const result = providers
        ?.map(provider => {
            return schemeTypes
                .map(({ extractScheme, providerTidToResults }) => {
                    // tracking validity, unit mapping, column class mapping, scheme count, scheme map
                    const validKpiCodeSchemeTypeMethodMap: Record<string, 1> = {};
                    const kpiCodeSchemeTypeMethodToUnit: Record<string, Unit> = {};
                    const kpiCodeSchemeTypeMethodToClassName: Record<string, string> = {};
                    let schemeCount = 1;
                    let validSchemeMap: Record<string, 1> = {};
                    let validSchemeWithSchemeTypeMap: Record<string, 1> = {};

                    const _results = providerTidToResults?.[provider?.tid];
                    if (!_results?.length) return null;

                    const yearToResults = _.groupBy(_results, d => d.year);
                    let years = _.orderBy(_.keys(yearToResults), d => d, "asc");

                    if (!years?.length) return null;

                    years = years.filter(year => {
                        if (year == null) return true;

                        const yearAsInt = parseInt(year);
                        const take =
                            (yearStart != null ? yearAsInt > yearStart : true) &&
                            (yearEnd != null ? yearAsInt <= yearEnd : true);

                        return take;
                    });

                    let data: {
                        [x: string]: string | number;
                        kpiCode: string;
                        scheme: string;
                        schemeType: string;
                        year: string;
                        unit: Unit;
                    }[] = [];

                    data = _.flattenDeep(
                        years.map(year => {
                            const yearResults = yearToResults?.[year];
                            if (!yearResults?.length) return [];

                            const _schemes = extractScheme
                                ? _.orderBy(_.uniq(yearResults?.map(d => d.scheme)), d => d, "asc")
                                : ["application"];

                            const schemeWithSchemeTypes = extractScheme
                                ? _.orderBy(
                                      _.uniqBy(
                                          yearResults?.map(d => {
                                              return {
                                                  scheme: d.scheme,
                                                  schemeType: d.schemeType
                                              };
                                          }),
                                          d => `${d.scheme} ### ${d.schemeType}`
                                      ),
                                      d => d.scheme,
                                      "asc"
                                  )
                                : [
                                      {
                                          scheme: "application",
                                          schemeType: "WSP"
                                      }
                                  ];

                            // merge with existing schemes
                            if (extractScheme) {
                                _schemes?.forEach(scheme => {
                                    validSchemeMap[scheme] = 1;
                                });
                                schemeWithSchemeTypes?.forEach(d => {
                                    validSchemeWithSchemeTypeMap[`${d.scheme} ### ${d.schemeType}`] = 1;
                                });
                            }

                            // count uniq items of scheme and scheme types
                            schemeCount = extractScheme ? _.keys(validSchemeWithSchemeTypeMap)?.length : 1;

                            // if (extractScheme) {
                            //     console.log(validSchemeWithSchemeTypeMap, schemeCount);
                            // }

                            // get all schemes from merged results
                            const schemes = extractScheme
                                ? _.orderBy(_.keys(validSchemeMap), d => d, "asc")
                                : ["application"];

                            const schemeToResults = extractScheme
                                ? _.groupBy(yearResults, d => d.scheme)
                                : {
                                      application: yearResults
                                  };

                            // const schemeSchemeTypeToResults = extractScheme
                            //     ? _.groupBy(yearResults, d => `${d.scheme} ### ${d.schemeType}`)
                            //     : {
                            //           "application ### WSP": yearResults
                            //       };

                            return schemes?.map(scheme => {
                                const schemeResult = schemeToResults[scheme];
                                if (!schemeResult?.length)
                                    return {
                                        scheme,
                                        schemeType: null,
                                        year,
                                        kpiCode: null,
                                        unit: null
                                    };

                                const kpiCodeSchemeTypeToResults = _.groupBy(schemeResult, d =>
                                    _.join([d.kpiCode, d.schemeType], "###")
                                );

                                const kpiParams = extractScheme ? schemeKpiParams : providerKpiParams;

                                return kpiParams
                                    .map(({ name, subCodes, kpiCode, method, schemeType }) => {
                                        const schemeTypeBasedResult = schemeResult?.filter(
                                            d => d.schemeType === schemeType
                                        );

                                        if (!schemeTypeBasedResult?.length) return null;

                                        const kpiResults: KpiReport[] = _.flattenDeep(
                                            subCodes
                                                .map(subCode => {
                                                    return kpiCodeSchemeTypeToResults[
                                                        _.join([subCode, schemeType], "###")
                                                    ];
                                                })
                                                .filter(d => d != null)
                                        );

                                        // kpiCodeSchemeTypeToResults[_.join([kpiCode, schemeType], "###")];
                                        const baseValues =
                                            kpiResults?.map(d => {
                                                return {
                                                    ...d,
                                                    ...getBaseValue(d.value, d.unit)
                                                };
                                            }) ?? (EMPTY_ARRAY as KpiReport[]);
                                        // const unit =
                                        //     method === "count"
                                        //         ? ("Count" as Unit)
                                        //         : (_.uniq(baseValues?.map(d => d.unit))?.[0] as Unit);

                                        // when method is custom,
                                        // it returns value, unit, valid, unit text, type class name
                                        const {
                                            value: _value,
                                            isValid,
                                            typeClassName,
                                            unit: _unit,
                                            unitText: _unitText,
                                            unitTransformForTableColumnDisplay
                                        } = calculateLogic({
                                            method: method as TableMethod,
                                            baseValues,
                                            provider,
                                            category: categoryChart?.category,
                                            kpis: subCodes.map(kpiCode => {
                                                return [kpiCode, schemeType];
                                            })
                                        });

                                        // console.log("baseValues", baseValues, _value, unit);

                                        const value =
                                            _value == null || _unit == null
                                                ? null
                                                : typeof _value === "number"
                                                ? roundValue(_value, _unit, true, unitTransformForTableColumnDisplay)
                                                      ?.value
                                                : VALUE_STATUS_NAMING?.[_value] ?? _value;

                                        // console.log({
                                        //     value,
                                        //     _value,
                                        //     isValid,
                                        //     typeClassName,
                                        //     unit: _unit,
                                        //     unitText: _unitText
                                        // });

                                        const unit = roundValue(0, _unit, true, unitTransformForTableColumnDisplay)
                                            ?.unit as Unit;
                                        let unitText = _unitText == null ? null : unit;

                                        // if unit text is recorded before, but this time we cannot find it, use previous record
                                        if (
                                            (unitText == null || unitText?.trim() === "") &&
                                            kpiCodeSchemeTypeMethodToUnit[
                                                _.join([kpiCode, schemeType, method], "###")
                                            ] != null &&
                                            kpiCodeSchemeTypeMethodToUnit[
                                                _.join([kpiCode, schemeType, method], "###")
                                            ]?.trim() !== ""
                                        ) {
                                            unitText =
                                                kpiCodeSchemeTypeMethodToUnit[
                                                    _.join([kpiCode, schemeType, method], "###")
                                                ];
                                        }

                                        isValid === true &&
                                            (kpiCodeSchemeTypeMethodToUnit[
                                                _.join([kpiCode, schemeType, method], "###")
                                            ] = unitText);

                                        isValid === true &&
                                            (validKpiCodeSchemeTypeMethodMap[
                                                _.join([kpiCode, schemeType, method], "###")
                                            ] = 1);
                                        kpiCodeSchemeTypeMethodToClassName[
                                            _.join([kpiCode, schemeType, method], "###")
                                        ] = typeClassName;

                                        // console.log({
                                        //     scheme,
                                        //     year,
                                        //     providerName: provider?.name,
                                        //     kpiCode,
                                        //     [`${name} ${
                                        //         unitText != null && unitText?.trim() !== "" ? `(${unitText})` : ""
                                        //     }`]: value,
                                        //     unit
                                        // });
                                        return {
                                            scheme,
                                            schemeType,
                                            year,
                                            providerName: provider?.name,
                                            kpiCode,
                                            [`${name} ${
                                                unitText != null && unitText?.trim() !== "" ? `(${unitText})` : ""
                                            }`]: value,
                                            unit
                                        };
                                    })
                                    .filter(d => d != null);
                            });
                        })
                    );

                    if (_.keys(validKpiCodeSchemeTypeMethodMap)?.length === 0) return null;

                    const title = !extractScheme ? "Provider-wide" : "Scheme breakdown";
                    const yearSchemeToResults = !extractScheme
                        ? _.groupBy(data, d => _.join([d.year, d.scheme], "###"))
                        : _.groupBy(data, d => _.join([d.year, d.schemeType, d.scheme], "###"));

                    const yearSchemeResults = _.values(yearSchemeToResults).map(results => {
                        const mergedResults = results.reduce(
                            //<
                            // {
                            //     [x: string]: string | number;
                            //     kpiCode: string[];
                            //     scheme: string;
                            //     year: string;
                            //     unit: Unit[];
                            // }
                            //>
                            (prev, current) => {
                                prev = {
                                    ...prev,
                                    ...current
                                };
                                return prev;
                            },
                            {} as {
                                [x: string]: string | number;
                                schemeType: string;
                                scheme: string;
                                year: string;
                                // unit: Unit[];
                            }
                        );

                        return mergedResults;
                    });

                    // console.log(
                    //     data,
                    //     yearSchemeResults,
                    //     kpiCodeSchemeTypeMethodToUnit,
                    //     validKpiCodeSchemeTypeMethodMap,
                    //     kpiCodeSchemeTypeMethodToClassName
                    // );

                    const rowClassNames = ["string-mid", "string-mid"];
                    const columnClassNames = ["", "reporting-table-organisation"];

                    const hasALotSchemeTypes =
                        kpis?.find(d => d?.[4] === "both-scheme-provider") != null ||
                        _.uniq(yearSchemeResults?.map(d => d.schemeType)?.filter(d => d != null)).length > 1;

                    const columns: Column[] = [
                        {
                            Header: "Year",
                            accessor: "year"
                        },
                        { Header: "Organisation", accessor: "providerName" },
                        extractScheme
                            ? {
                                  Header: "Scheme",
                                  accessor: "scheme"
                              }
                            : null,
                        extractScheme && hasALotSchemeTypes
                            ? {
                                  Header: "Scheme type",
                                  accessor: "schemeType"
                              }
                            : null
                        //     ,
                        // {
                        //     Header: valueColumnName,
                        //     accessor: valueColumnName
                        // }
                    ].filter(d => d != null);

                    extractScheme === true && rowClassNames.push("string-mid");
                    extractScheme === true && columnClassNames.push("reporting-table-scheme");
                    extractScheme === true && hasALotSchemeTypes === true && rowClassNames.push("string-mid");
                    extractScheme === true &&
                        hasALotSchemeTypes === true &&
                        columnClassNames.push("reporting-table-scheme-type");

                    const params = extractScheme ? schemeKpiParams : providerKpiParams;
                    // console.log(
                    //     provider,
                    //     validKpiCodeSchemeTypeMethodMap,
                    //     params,
                    //     yearSchemeResults,
                    //     kpiCodeSchemeTypeMethodToUnit,
                    //     validKpiCodeSchemeTypeMethodMap,
                    //     kpiCodeSchemeTypeMethodToClassName
                    // );

                    const validParams = params?.filter(
                        d => validKpiCodeSchemeTypeMethodMap?.[_.join([d.kpiCode, d.schemeType, d.method], "###")] === 1
                    );

                    const nameToKpiParams = _.groupBy(validParams, d => d.name);

                    const kpiNames = _.uniq(validParams?.map(d => d.name));

                    kpiNames?.forEach(kpiName => {
                        const kpis = nameToKpiParams?.[kpiName];

                        // assume we can we have multi scheme types but share the same methods, and codes
                        const schemeTypeKpis = _.uniqBy(kpis, d => d.schemeType);
                        const unit = schemeTypeKpis
                            ?.map(
                                kpi =>
                                    kpiCodeSchemeTypeMethodToUnit[
                                        _.join([kpi?.kpiCode, kpi?.schemeType, kpi?.method], "###")
                                    ]
                            )
                            ?.find(d => d != null);
                        const kpiCode = schemeTypeKpis?.map(kpi => kpi?.kpiCode)?.find(d => d != null);
                        const method = schemeTypeKpis?.map(kpi => kpi?.method)?.find(d => d != null);
                        const rowClassName = schemeTypeKpis
                            ?.map(
                                kpi =>
                                    kpiCodeSchemeTypeMethodToClassName[
                                        _.join([kpi?.kpiCode, kpi?.schemeType, kpi?.method], "###")
                                    ]
                            )
                            ?.find(d => d != null);

                        // setup colum
                        const valueColumnName = `${kpiName} ${unit != null && unit?.trim() !== "" ? `(${unit})` : ""}`;
                        columns.push({
                            Header: valueColumnName,
                            accessor: valueColumnName,
                            Cell: ({ value }) => {
                                return value == null
                                    ? ""
                                    : typeof value === "number"
                                    ? // ? formatNumber(value, undefined, unit, categoryChart)
                                      formatTableColumnNumber(
                                          {
                                              kpiCode,
                                              method: method as TableMethod,
                                              name: kpiName
                                              // schemeType
                                          },
                                          value,
                                          unit
                                      )
                                    : `${uppercaseFirst(value as string)}`;
                                // : `${(value as string)?.toString()?.[0]?.toUpperCase()}${(value as string)
                                //       ?.toString()
                                //       ?.substring(1)
                                //       ?.toLowerCase()}`;
                            }
                        });

                        rowClassName != null && rowClassNames.push(rowClassName);
                    });

                    // validParams
                    //     // ?.filter(
                    //     //     d =>
                    //     //         validKpiCodeSchemeTypeMethodMap?.[
                    //     //             _.join([d.kpiCode, d.schemeType, d.method], "###")
                    //     //         ] === 1
                    //     // )
                    //     ?.forEach(({ kpiCode, method, name, schemeType }) => {
                    //         const unit = kpiCodeSchemeTypeMethodToUnit[_.join([kpiCode, schemeType, method], "###")];
                    //         const valueColumnName = `${name} ${unit != null && unit?.trim() !== "" ? `(${unit})` : ""}`;
                    //         columns.push({
                    //             Header: valueColumnName,
                    //             accessor: valueColumnName,
                    //             Cell: ({ value }) => {
                    //                 return value == null
                    //                     ? ""
                    //                     : typeof value === "number"
                    //                     ? // ? formatNumber(value, undefined, unit, categoryChart)
                    //                       formatTableColumnNumber(
                    //                           {
                    //                               kpiCode,
                    //                               method: method as TableMethod,
                    //                               name
                    //                               // schemeType
                    //                           },
                    //                           value,
                    //                           unit
                    //                       )
                    //                     : `${uppercaseFirst(value as string)}`;
                    //                 // : `${(value as string)?.toString()?.[0]?.toUpperCase()}${(value as string)
                    //                 //       ?.toString()
                    //                 //       ?.substring(1)
                    //                 //       ?.toLowerCase()}`;
                    //             }
                    //         });

                    //         kpiCodeSchemeTypeMethodToClassName[_.join([kpiCode, schemeType, method], "###")] != null &&
                    //             rowClassNames.push(
                    //                 kpiCodeSchemeTypeMethodToClassName[_.join([kpiCode, schemeType, method], "###")]
                    //             );
                    //     });

                    // console.log(yearSchemeResults);

                    return {
                        waterProvider: provider,
                        header: {
                            processed: title,
                            value: title
                        },
                        categoryName: categoryChart?.category?.name,
                        type: extractScheme ? "scheme-breakdown" : "provider-wide",
                        data: {
                            columns,
                            data: yearSchemeResults,
                            enabledDownload: true,
                            columnClassNames,
                            // downloadFileName: `${provider?.name} - ${title} - ${categoryChart?.category?.name}.csv`,
                            downloadFileName: `${title} - ${categoryChart?.category?.name}`,
                            rowClassNames,
                            className: "reporting-table",
                            pageSize: extractScheme ? schemeCount : yearSchemeResults?.length
                            // pageSize: data?.length
                        }
                    } as TableBase & {
                        header?: LongText;
                        footer?: LongText;
                    };
                })
                .filter(d => d != null);
        })
        .filter(d => d != null);

    return result;
};

export const fetchProviderTableData = async ({
    fetchParams,
    metadata,
    categoryChart,
    providers,
    kpis,
    category,
    locationAdminIds,
    latestYear
}: CategoryChartQueryParam): Promise<TableKpiProvider> => {
    const tableData = await getTable({
        fetchParams,
        metadata,
        categoryChart,
        providers,
        kpis,
        category,
        locationAdminIds,
        latestYear
    });

    // const years = _.orderBy(
    //     results.map(d => d.year),
    //     d => d,
    //     "asc"
    // );

    // const data = [
    //     {
    //         col1: "Hello",
    //         col2: "World",
    //         col3: "World"
    //     },
    //     {
    //         col1: "react-table",
    //         col2: "rocks",
    //         col3: "rocks"
    //     },
    //     {
    //         col1: "whatever",
    //         col2: "you want",
    //         col3: "you want"
    //     },
    //     {
    //         col1: "whatever2",
    //         col2: "you want 2",
    //         col3: "you want"
    //     }
    // ];

    // const columns = [
    //     {
    //         Header: "Column 1",
    //         accessor: "col1" // accessor is the "key" in the data
    //     },
    //     {
    //         Header: "Com 2,3",
    //         columns: [
    //             {
    //                 Header: "Column 2",
    //                 accessor: "col2"
    //             },
    //             {
    //                 Header: "Column 2",
    //                 accessor: "col3"
    //             }
    //         ]
    //     }
    // ];

    return {
        vis: tableData?.[0]
        // [
        //     {
        //         header: {
        //             // format: "",
        //             processed: "",
        //             value: ""
        //         },
        //         data: {
        //             columns,
        //             data,
        //             rowClassNames: ["string", "string", "number"],
        //             pageSize: data?.length
        //         }
        //     }
        // ]
    };
};
